import { useEffect, lazy, Suspense } from 'react';
import { parse } from 'query-string';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';
import { legacyC2foAdminUiConfig } from '@c2fo/common-config';
import { setToken, getToken } from './app/utils/token';
import { Navigation } from './app/components/Navigation/Navigation';
import { EnvironmentBanner } from './app/components/EnvironmentBanner/EnvironmentBanner';
import { config } from './app/config';
import { ErrorBoundary } from './app/components/ErrorBoundary';
import { AdminSplitProvider } from './app/providers/SplitProvider/SplitProvider';
import { ComingSoonRoute } from './app/feature-flags';
import { PageFlags } from './app/feature-flags/pageFlags';
import { ChatPage, RedshiftChatPage, InsightsPage, InsightsFeedbackPage } from './app/pages';
import PageTracker from './app/reporting/PageTracker';
import SomethingsWrong from './app/pages/SomethingsWrong';
import { FileDelivery } from './app/pages/FileDelivery/FileDelivery';
const AdminUser = lazy(() => import('./app/pages/AdminUser').then((mod) => ({ default: mod.AdminUser })));
const MFAPage = lazy(() => import('./app/pages/AdminUser/MFA').then((mod) => ({ default: mod.MFAPage })));

// Lazy load pages for better performance
const MarketOverview = lazy(() =>
  import('./app/pages/MarketOverview/MarketOverview').then((mod) => ({ default: mod.MarketOverview })),
);
const Divisions = lazy(() => import('./app/pages/Divisions/Divisions').then((mod) => ({ default: mod.Divisions })));
const DivisionDetails = lazy(() =>
  import('./app/pages/Divisions/DivisionDetails').then((mod) => ({ default: mod.DivisionDetails })),
);
const Markets = lazy(() => import('./app/pages/Markets/Markets').then((mod) => ({ default: mod.Markets })));
const MarketDetails = lazy(() =>
  import('./app/pages/MarketDetails/MarketDetails').then((mod) => ({ default: mod.MarketDetails })),
);
const Users = lazy(() => import('./app/pages/Users/Users').then((mod) => ({ default: mod.Users })));
const CreateUser = lazy(() => import('./app/pages/Users/CreateUser').then((mod) => ({ default: mod.CreateUser })));
const UserDetail = lazy(() => import('./app/pages/UserDetail/UserDetail').then((mod) => ({ default: mod.UserDetail })));
const QueryExecutionPage = lazy(() =>
  import('./app/pages/QueryExecutionPage/QueryExecutionPage').then((mod) => ({ default: mod.QueryExecutionPage })),
);
const Accounts = lazy(() => import('./app/pages/Accounts/Accounts').then((mod) => ({ default: mod.Accounts })));
const AccountDetails = lazy(() =>
  import('./app/pages/Accounts/AccountDetails').then((mod) => ({ default: mod.AccountDetails })),
);
const QueriesPage = lazy(() =>
  import('./app/pages/QueriesPage/QueriesPage').then((mod) => ({ default: mod.QueriesPage })),
);
const Eslap = lazy(() => import('./app/pages/Eslap/Eslap').then((mod) => ({ default: mod.Eslap })));
const Award = lazy(() => import('./app/pages/Award').then((mod) => ({ default: mod.Award })));

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-gray-600">Loading...</div>
  </div>
);

const loginRoute = `${legacyC2foAdminUiConfig.LEGACY_C2FO_ADMIN_UI_URL}/login?redirect-to=${config.NEW_ADMIN_UI_URL}/login-callback&tokenQueryParam=authorization`;

// Login page component that redirects to legacy login
const LoginPage = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const showTimeoutMessage = params.get('showTimeoutMessage');

    if (showTimeoutMessage === 'true') {
      // For timeout messages, redirect to legacy login with timeout message AND callback to new admin
      const timeoutLoginUrl = `${legacyC2foAdminUiConfig.LEGACY_C2FO_ADMIN_UI_URL}/login?showTimeoutMessage=true&redirect-to=${config.NEW_ADMIN_UI_URL}/login-callback&tokenQueryParam=authorization`;
      window.location.assign(timeoutLoginUrl);
    } else {
      // Normal login flow
      window.location.assign(loginRoute);
    }
  }, []);
  return null;
};

// Login callback component
const LoginCallbackPage = () => {
  const queryParams = parse(window.location.search);
  const authorization = queryParams.authorization ? queryParams.authorization : '';
  const jwt = authorization.toString();

  if (authorization.length) {
    setToken(jwt);
  }

  return <Navigate to="/" replace />;
};

const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  if (!getToken()) {
    return <Navigate to="/login" replace />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
};

/** Temporary: redirect home to first visible page until more pages are enabled via feature flags. */
const TEMPORARY_HOME_REDIRECT_TO = '/users' as const;

/** Remount Users when search params change so page search input is filled from URL (e.g. nav search → /users?q=...). */
const UsersWithSearchKey = () => {
  const location = useLocation();
  return <Users key={location.search} />;
};

const browserRouter = createBrowserRouter([
  {
    children: [
      // Unauthenticated routes
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: 'login-callback',
        element: <LoginCallbackPage />,
      },
      // Authenticated routes
      {
        errorElement: <SomethingsWrong />,
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AdminSplitProvider>
              <EnvironmentBanner />
              <PageTracker />
              <Navigation />
              <ScrollRestoration />
              <Outlet />
            </AdminSplitProvider>
          </Suspense>
        ),
        children: [
          {
            path: '/',
            element: <Navigate to={TEMPORARY_HOME_REDIRECT_TO} replace />,
          },
          {
            path: 'chat',
            element: (
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'redshift-chat',
            element: (
              <ProtectedRoute>
                <RedshiftChatPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'query/:id',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <QueryExecutionPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'queries',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <QueriesPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'divisions',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.DIVISIONS_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Divisions />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'market-overview',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.MARKET_OVERVIEW_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <MarketOverview />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'markets',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.MARKETS_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Markets />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'markets/:marketUuid',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.MARKETS_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <MarketDetails />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'users/new',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <CreateUser />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'users/:uuid',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <UserDetail />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'users',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <UsersWithSearchKey />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'accounts',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.ACCOUNTS_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Accounts />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'accounts/:accountId',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.ACCOUNTS_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <AccountDetails />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'divisions/:makerUuid/:divisionId',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.DIVISIONS_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <DivisionDetails />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'insights',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.INSIGHTS_PAGE}>
                  <InsightsPage />
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'insights/feedback',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.INSIGHTS_PAGE}>
                  <InsightsFeedbackPage />
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'admin-user',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <AdminUser />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'admin-user/mfa',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <MFAPage />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'award',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.AWARD_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Award />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'file-delivery/*',
            element: (
              <ProtectedRoute>
                <Suspense fallback={<LoadingFallback />}>
                  <FileDelivery />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'eslap',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.ESLAP_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Eslap />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
          {
            path: 'eslap/org/:makerOrgUuid/fileset/:filesetConfigId',
            element: (
              <ProtectedRoute>
                <ComingSoonRoute splitName={PageFlags.ESLAP_PAGE}>
                  <Suspense fallback={<LoadingFallback />}>
                    <Eslap />
                  </Suspense>
                </ComingSoonRoute>
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
]);

const Router = () => {
  return <RouterProvider router={browserRouter} />;
};

export default Router;
